import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
  ogUrl?: string;
  type?: string;
  noindex?: boolean;
  /** Path without language prefix, e.g. "/" or "/blog" */
  canonicalPath?: string;
}

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "reelistiq",
  alternateName: "reelistiq — Micro Drama Agency",
  url: "https://reelistiq.com",
  logo: "https://reelistiq.com/og-image.png",
  image: "https://reelistiq.com/og-image.png",
  email: "hello@reelistiq.com",
  founder: { "@type": "Person", name: "Meriem Rebai" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Kollwitzstraße 76",
    addressLocality: "Berlin",
    postalCode: "10435",
    addressCountry: "DE",
  },
  sameAs: [
    "https://www.linkedin.com/company/reelistiq",
    "https://www.instagram.com/reelistiq",
    "https://www.tiktok.com/@reelistiq",
  ],
  knowsAbout: [
    "Micro Drama",
    "Vertical Drama",
    "Brand Integration",
    "Social Media Storytelling",
    "TikTok Series",
  ],
};

export function SEOHead({
  title = "reelistiq — Micro Drama Agentur für Brands | Deutschland",
  description = "Deutschlands erste Micro Drama Agentur: Wir bringen Marken in serielle Kurzformate auf TikTok und Instagram — Brand Integration, Custom IP und Full Production.",
  ogImage = "https://reelistiq.com/og-image.png",
  ogUrl = "https://reelistiq.com",
  type = "website",
  noindex = false,
  canonicalPath,
}: SEOHeadProps) {
  const baseUrl = "https://reelistiq.com";
  const { language } = useLanguage();

  // Build canonical URL: must exactly match the corresponding hreflang URL
  const langPrefix = `/${language}`;
  const pathSuffix = canonicalPath && canonicalPath !== "/" ? canonicalPath : "";
  const canonicalUrl = canonicalPath ? `${baseUrl}${langPrefix}${pathSuffix}` : ogUrl;

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:site_name" content="reelistiq" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={language === "de" ? "de_DE" : "en_US"} />
      <meta property="og:locale:alternate" content={language === "de" ? "en_US" : "de_DE"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@reelistiq" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* hreflang alternates — canonical must exactly match the current lang hreflang */}
      {canonicalPath && (
        <>
          <link rel="canonical" href={canonicalUrl} />
          <link rel="alternate" hrefLang="de" href={`${baseUrl}/de${pathSuffix}`} />
          <link rel="alternate" hrefLang="en" href={`${baseUrl}/en${pathSuffix}`} />
          <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/de${pathSuffix}`} />
        </>
      )}

      {/* Organization JSON-LD on every page */}
      <script type="application/ld+json">{JSON.stringify(ORGANIZATION_SCHEMA)}</script>
    </Helmet>
  );
}
